import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Upload,
  Loader2,
  AlertTriangle,
  FileBox,
  PenLine,
  FileCheck,
  X,
  RefreshCw,
  Minus,
  Plus,
  Lock,
  Layers,
  FlaskConical,
  Palette,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { StratoButton } from "@/components/common/StratoButton";
import { MicroLabel } from "@/components/common/MicroLabel";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { MATERIAL_IMAGES } from "@/assets/materials";
import {
  QUOTE_STEPS,
  START_OPTIONS,
  FILE_FORMATS,
  FILE_MAX_LABEL,
  PIECE_TYPES,
  USE_USES,
  USE_GOALS,
  MATERIAL_OPTIONS,
  QUOTE_MATERIAL,
  FINISH_OPTIONS,
  QUANTITY_PRESETS,
  URGENCY_OPTIONS,
} from "@/content/quoteContent";

const emailValid = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
const ACCEPTED_EXT = ["stl", "step", "stp", "3mf"];
const startIcons = { file: FileBox, nofile: PenLine };

const emptyData = {
  start: null,
  fileName: "",
  fileStatus: "idle", // idle | uploading | done | error
  fileProgress: 0,
  description: "",
  pieceType: "",
  pieceTypeOther: "",
  use: "",
  useOther: "",
  goal: "",
  materialType: "pla", // pla | advice | custom
  materialRequest: "",
  color: 0,
  customColor: false,
  colorRequest: "",
  finish: "Standard",
  quantity: 1,
  urgency: "Standard",
  desiredDate: "",
  notes: "",
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  company: "",
  hp: "", // honeypot (hidden)
  acceptTerms: false,
  acceptLawful: false,
};

export const QuoteConfigurator = () => {
  const [step, setStep] = useState(0);
  const [data, setData] = useState(emptyData);
  const [view, setView] = useState("form"); // form | loading | error | success
  const [stepError, setStepError] = useState("");
  const [submitMsg, setSubmitMsg] = useState("");
  const fileInputRef = useRef(null);
  const progressRef = useRef(null);

  const set = (patch) => setData((d) => ({ ...d, ...patch }));
  const last = QUOTE_STEPS.length - 1;

  useEffect(() => () => progressRef.current && clearInterval(progressRef.current), []);

  /* ---------- validation per step ---------- */
  const validateStep = (s) => {
    switch (s) {
      case 0:
        if (!data.start) return "Scegli da cosa vuoi partire.";
        return "";
      case 1:
        if (data.start === "file") {
          if (data.fileStatus !== "done")
            return "Seleziona un file STL, STEP, STP o 3MF.";
        } else if (!data.description.trim()) {
          return "Descrivi il pezzo o aggiungi misure indicative.";
        }
        return "";
      case 2:
        if (!data.pieceType) return "Scegli il tipo di pezzo.";
        if (data.pieceType === "Altro" && !data.pieceTypeOther.trim())
          return "Specifica il tipo di pezzo.";
        return "";
      case 3:
        if (!data.use) return "Scegli l'uso previsto.";
        if (data.use === "Altro" && !data.useOther.trim())
          return "Specifica l'uso previsto.";
        if (!data.goal) return "Scegli l'obiettivo principale.";
        return "";
      case 4:
        if (!data.materialType) return "Scegli il materiale preferito.";
        if (data.customColor && !data.colorRequest.trim())
          return "Scrivi il colore che hai in mente.";
        if (!data.finish) return "Scegli la finitura desiderata.";
        return "";
      case 5:
        if (!(data.quantity >= 1)) return "Inserisci un numero intero positivo.";
        if (!data.urgency) return "Scegli l'urgenza.";
        return "";
      default:
        return "";
    }
  };

  const next = () => {
    const err = validateStep(step);
    if (err) {
      setStepError(err);
      return;
    }
    setStepError("");
    setStep((s) => Math.min(s + 1, last));
  };
  const back = () => {
    setStepError("");
    setStep((s) => Math.max(s - 1, 0));
  };

  /* ---------- file upload UI state ---------- */
  const startUpload = (name) => {
    set({ fileName: name, fileStatus: "uploading", fileProgress: 0 });
    if (progressRef.current) clearInterval(progressRef.current);
    progressRef.current = setInterval(() => {
      setData((d) => {
        const nextP = Math.min(100, d.fileProgress + 14);
        if (nextP >= 100) {
          clearInterval(progressRef.current);
          return { ...d, fileProgress: 100, fileStatus: "done" };
        }
        return { ...d, fileProgress: nextP };
      });
    }, 130);
  };

  const onFilePick = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    setStepError("");
    const ext = (f.name.split(".").pop() || "").toLowerCase();
    if (!ACCEPTED_EXT.includes(ext)) {
      if (progressRef.current) clearInterval(progressRef.current);
      set({ fileName: f.name, fileStatus: "error", fileProgress: 0 });
    } else {
      startUpload(f.name);
    }
    e.target.value = "";
  };

  const removeFile = () => {
    if (progressRef.current) clearInterval(progressRef.current);
    set({ fileName: "", fileStatus: "idle", fileProgress: 0 });
  };
  const openPicker = () => fileInputRef.current && fileInputRef.current.click();

  const submit = () => {
    // client-side validation with friendly messages
    if (!data.firstName.trim()) {
      setSubmitMsg("Inserisci il tuo nome.");
      return;
    }
    if (!emailValid(data.email)) {
      setSubmitMsg("Inserisci un indirizzo email valido.");
      return;
    }
    if (!data.acceptTerms) {
      setSubmitMsg("Accetta Termini, Privacy Policy e Cookie Policy per inviare la richiesta.");
      return;
    }
    if (!data.acceptLawful) {
      setSubmitMsg("Conferma che la richiesta riguarda un utilizzo lecito e non pericoloso.");
      return;
    }
    setSubmitMsg("");
    setView("loading");
    window.setTimeout(() => setView("success"), 1400);
  };

  /* ---------- state views ---------- */
  if (view === "loading") {
    return (
      <StateShell testId="quote-loading">
        <Loader2 className="h-10 w-10 animate-spin text-strato-blue" />
        <h2 className="mt-6 font-display text-2xl font-medium text-strato-graphite">
          Invio della richiesta…
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Stiamo preparando il riepilogo tecnico del tuo progetto.
        </p>
      </StateShell>
    );
  }

  if (view === "error") {
    return (
      <StateShell testId="quote-error">
        <span className="grid h-14 w-14 place-items-center rounded-2xl border border-strato-gray-100 bg-strato-gray-50 text-amber-600">
          <AlertTriangle className="h-7 w-7" strokeWidth={1.6} />
        </span>
        <h2 className="mt-6 font-display text-2xl font-medium text-strato-graphite">
          Qualcosa è andato storto
        </h2>
        <p className="mt-2 max-w-[42ch] text-sm text-muted-foreground">
          Non siamo riusciti a completare la richiesta. Riprova tra poco. I dati
          inseriti sono rimasti nella pagina.
        </p>
        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          <StratoButton data-testid="quote-error-retry" onClick={() => setView("form")}>
            <RefreshCw className="h-4 w-4" />
            Riprova
          </StratoButton>
          <StratoButton as={Link} to="/" variant="secondary">
            Torna alla homepage
          </StratoButton>
        </div>
      </StateShell>
    );
  }

  if (view === "success") {
    return (
      <StateShell testId="quote-success">
        <span className="grid h-14 w-14 place-items-center rounded-2xl bg-[var(--strato-blue-soft)] text-strato-blue">
          <Check className="h-7 w-7" strokeWidth={2.2} />
        </span>
        <h2 className="mt-6 font-display text-3xl font-medium text-strato-graphite">
          Richiesta inviata.
        </h2>
        <p className="mt-2 max-w-[46ch] text-sm text-muted-foreground">
          Grazie {data.firstName || ""}. Un tecnico esaminerà il tuo progetto e ti
          risponderà con una valutazione tecnica entro 12 ore lavorative.
        </p>
        <p
          data-testid="quote-success-next-step"
          className="mt-6 rounded-xl border border-strato-gray-100 bg-strato-gray-50 px-4 py-3 text-sm text-strato-graphite"
        >
          Il codice richiesta verrà riportato nella risposta email.
        </p>
        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          <StratoButton as={Link} to="/" data-testid="quote-success-home">
            Torna alla homepage
          </StratoButton>
          <StratoButton
            as={Link}
            to="/stato-richiesta"
            variant="secondary"
            data-testid="quote-success-follow"
          >
            Segui la richiesta
          </StratoButton>
        </div>
      </StateShell>
    );
  }

  /* ================= STEP 1 — start ================= */
  const StepStart = (
    <StepWrap
      micro="STEP 01 · PUNTO DI PARTENZA"
      title="Da cosa partiamo?"
      subtitle="Scegli se parti da un modello 3D già pronto oppure da una descrizione. Il preventivo resta tecnico e personalizzato, non automatico."
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {START_OPTIONS.map((opt) => {
          const Icon = startIcons[opt.id];
          const active = data.start === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              data-testid={`quote-start-${opt.id}`}
              onClick={() => set({ start: opt.id })}
              className={cn(
                "flex flex-col rounded-2xl border p-6 text-left transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--strato-focus-ring)]",
                active
                  ? "border-strato-blue bg-[var(--strato-blue-soft)]"
                  : "border-strato-gray-100 bg-strato-white hover:border-strato-gray-300",
              )}
            >
              <span
                className={cn(
                  "grid h-11 w-11 place-items-center rounded-xl border",
                  active
                    ? "border-strato-blue/30 bg-white text-strato-blue"
                    : "border-strato-gray-100 bg-strato-gray-50 text-strato-graphite",
                )}
              >
                <Icon className="h-5 w-5" strokeWidth={1.5} />
              </span>
              <span className="mt-5 font-display text-lg font-medium text-strato-graphite">
                {opt.title}
              </span>
              <span className="mt-2 text-sm leading-[1.55] text-muted-foreground">
                {opt.description}
              </span>
              <MicroLabel className="mt-4 text-strato-gray-300">{opt.hint}</MicroLabel>
            </button>
          );
        })}
      </div>
    </StepWrap>
  );

  /* ================= STEP 2 — file or reference ================= */
  const StepInput = (
    <StepWrap
      micro="STEP 02 · PROGETTO"
      title={data.start === "file" ? "Carica il tuo file 3D" : "Descrivi il pezzo"}
      subtitle={
        data.start === "file"
          ? `Formati accettati: ${FILE_FORMATS.join(", ")}. Dimensione massima ${FILE_MAX_LABEL}.`
          : "Puoi continuare anche senza file. Inserisci funzione, misure e riferimenti disponibili: ti aiuteremo a capire come procedere."
      }
    >
      {data.start === "file" ? (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept=".stl,.step,.stp,.3mf"
            onChange={onFilePick}
            className="hidden"
            data-testid="quote-file-input"
          />

          {data.fileStatus === "idle" ? (
            <button
              type="button"
              data-testid="quote-dropzone"
              onClick={openPicker}
              className="flex w-full flex-col items-center justify-center rounded-2xl border border-dashed border-strato-gray-300 bg-strato-gray-50 px-6 py-10 text-center transition-colors hover:border-strato-blue focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--strato-focus-ring)]"
            >
              <Upload className="h-6 w-6 text-strato-gray-300" strokeWidth={1.5} />
              <p className="mt-3 text-sm font-medium text-strato-graphite">
                Trascina qui il file o scegli dal dispositivo
              </p>
              <MicroLabel className="mt-1 text-muted-foreground">
                {FILE_FORMATS.join(" · ")} · MAX {FILE_MAX_LABEL}
              </MicroLabel>
            </button>
          ) : null}

          {data.fileStatus === "uploading" ? (
            <div
              data-testid="quote-file-uploading"
              className="rounded-2xl border border-strato-gray-100 bg-strato-white p-5"
            >
              <div className="flex items-center gap-3">
                <Loader2 className="h-5 w-5 animate-spin text-strato-blue" />
                <span className="flex-1 truncate text-sm font-medium text-strato-graphite">
                  {data.fileName}
                </span>
                <button
                  type="button"
                  data-testid="quote-file-remove"
                  onClick={removeFile}
                  aria-label="Rimuovi file"
                  className="grid h-8 w-8 place-items-center rounded-lg text-strato-gray-300 hover:bg-strato-gray-50 hover:text-strato-graphite"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-strato-gray-100">
                <div
                  className="h-full rounded-full bg-strato-blue transition-all duration-150"
                  style={{ width: `${data.fileProgress}%` }}
                />
              </div>
              <MicroLabel className="mt-2 text-muted-foreground">
                UPLOAD FILE {data.fileProgress}%
              </MicroLabel>
            </div>
          ) : null}

          {data.fileStatus === "done" ? (
            <div
              data-testid="quote-file-done"
              className="flex items-center gap-3 rounded-2xl border border-strato-gray-100 bg-strato-white p-5"
            >
              <span className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-xl bg-[var(--strato-blue-soft)] text-strato-blue">
                <FileCheck className="h-5 w-5" strokeWidth={1.6} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-strato-graphite">
                  {data.fileName}
                </p>
                <MicroLabel className="text-strato-blue">FILE CARICATO CORRETTAMENTE</MicroLabel>
              </div>
              <button
                type="button"
                data-testid="quote-file-remove"
                onClick={removeFile}
                aria-label="Rimuovi file"
                className="grid h-9 w-9 place-items-center rounded-lg text-strato-gray-300 hover:bg-strato-gray-50 hover:text-strato-graphite"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : null}

          {data.fileStatus === "error" ? (
            <div
              data-testid="quote-file-error"
              className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-5"
            >
              <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-800">
                  Formato non supportato. Usa STL, STEP, STP o 3MF.
                </p>
                <p className="mt-1 text-xs text-amber-700">
                  Upload non completato. Riprova selezionando di nuovo il file.
                </p>
                <div className="mt-3 flex gap-2">
                  <StratoButton
                    variant="secondary"
                    data-testid="quote-file-retry"
                    onClick={openPicker}
                    className="px-4 py-2 text-[13px]"
                  >
                    <RefreshCw className="h-3.5 w-3.5" />
                    Scegli un altro file
                  </StratoButton>
                  <StratoButton
                    variant="ghost"
                    data-testid="quote-file-remove"
                    onClick={removeFile}
                    className="px-4 py-2 text-[13px]"
                  >
                    Annulla
                  </StratoButton>
                </div>
              </div>
            </div>
          ) : null}

          <div
            data-testid="quote-file-confidentiality"
            className="mt-4 flex items-start gap-3 rounded-xl border border-strato-gray-100 bg-strato-gray-50 px-4 py-3"
          >
            <Lock className="mt-0.5 h-4 w-4 flex-shrink-0 text-strato-blue" strokeWidth={1.6} />
            <p className="text-xs leading-[1.6] text-muted-foreground">
              Generiamo un link sicuro per il file. Il file resta riservato e viene usato
              solo per la valutazione tecnica e la gestione della richiesta. Non viene
              utilizzato come materiale pubblico o promozionale senza autorizzazione.
            </p>
          </div>
        </>
      ) : (
        <div>
          <label className="mb-2 block text-sm font-medium text-strato-graphite">
            Descrivi il pezzo <span className="text-strato-blue">*</span>
          </label>
          <textarea
            data-testid="quote-description"
            value={data.description}
            onChange={(e) => set({ description: e.target.value })}
            rows={6}
            placeholder="Descrivi funzione, misure indicative, vincoli e punti di fissaggio."
            className="w-full resize-none rounded-2xl border border-strato-gray-100 bg-strato-white p-5 text-sm leading-[1.6] text-strato-graphite outline-none transition-colors placeholder:text-strato-gray-300 focus:border-strato-blue focus-visible:ring-2 focus-visible:ring-[var(--strato-focus-ring)]"
          />
          <p className="mt-2 text-xs text-muted-foreground">
            Puoi continuare anche senza file. Inserisci funzione, misure e riferimenti
            disponibili: ti aiuteremo a capire come procedere.
          </p>
        </div>
      )}
    </StepWrap>
  );

  /* ================= STEP 3 — piece type ================= */
  const StepType = (
    <StepWrap
      micro="STEP 03 · COMPONENTE"
      title="Che tipo di pezzo è?"
      subtitle="Scegli la categoria che descrive meglio il componente."
    >
      <div className="max-w-md">
        <label className="mb-2 block text-sm font-medium text-strato-graphite">
          Tipo di pezzo
        </label>
        <Select value={data.pieceType} onValueChange={(v) => set({ pieceType: v })}>
          <SelectTrigger data-testid="quote-type-select" className="quote-select">
            <SelectValue placeholder="Seleziona il tipo di pezzo" />
          </SelectTrigger>
          <SelectContent>
            {PIECE_TYPES.map((opt) => (
              <SelectItem key={opt} value={opt} data-testid={`quote-type-option-${opt}`}>
                {opt}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {data.pieceType === "Altro" ? (
          <input
            data-testid="quote-type-other"
            value={data.pieceTypeOther}
            onChange={(e) => set({ pieceTypeOther: e.target.value })}
            placeholder="Specifica il tipo di pezzo"
            className="quote-input mt-4"
          />
        ) : null}
      </div>
    </StepWrap>
  );

  /* ================= STEP 4 — use & priority ================= */
  const StepUsage = (
    <StepWrap
      micro="STEP 04 · CONTESTO"
      title="Uso e priorità"
      subtitle="Ci aiuta a scegliere orientamento, riempimento e finitura."
    >
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <div>
          <label className="mb-2 block text-sm font-medium text-strato-graphite">
            Uso previsto
          </label>
          <Select value={data.use} onValueChange={(v) => set({ use: v })}>
            <SelectTrigger data-testid="quote-use-select" className="quote-select">
              <SelectValue placeholder="Seleziona l'uso previsto" />
            </SelectTrigger>
            <SelectContent>
              {USE_USES.map((opt) => (
                <SelectItem key={opt} value={opt} data-testid={`quote-use-option-${opt}`}>
                  {opt}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {data.use === "Altro" ? (
            <input
              data-testid="quote-use-other"
              value={data.useOther}
              onChange={(e) => set({ useOther: e.target.value })}
              placeholder="Specifica l'uso previsto"
              className="quote-input mt-4"
            />
          ) : null}
        </div>

        <div>
          <label className="mb-2 block text-sm font-medium text-strato-graphite">
            Obiettivo principale
          </label>
          <Select value={data.goal} onValueChange={(v) => set({ goal: v })}>
            <SelectTrigger data-testid="quote-goal-select" className="quote-select">
              <SelectValue placeholder="Seleziona l'obiettivo" />
            </SelectTrigger>
            <SelectContent>
              {USE_GOALS.map((opt) => (
                <SelectItem key={opt} value={opt} data-testid={`quote-goal-option-${opt}`}>
                  {opt}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </StepWrap>
  );

  /* ================= STEP 5 — technical preferences ================= */
  const StepMaterial = (
    <StepWrap
      micro="STEP 05 · FDM / PLA"
      title="Preferenze tecniche"
      subtitle="Tecnologia FDM. Il PLA è il nostro materiale principale. Puoi anche richiedere altri materiali o colori personalizzati su richiesta."
    >
      <div className="mb-6 inline-flex items-center gap-2 rounded-lg border border-strato-gray-100 bg-strato-gray-50 px-3 py-2">
        <MicroLabel className="text-muted-foreground">TECNOLOGIA</MicroLabel>
        <span className="text-sm font-medium text-strato-graphite">FDM</span>
      </div>

      <p className="text-sm font-medium text-strato-graphite">Materiale</p>
      <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-3">
        {MATERIAL_OPTIONS.map((m) => {
          const active = data.materialType === m.id;
          const Icon = m.id === "custom" ? FlaskConical : Layers;
          return (
            <button
              key={m.id}
              type="button"
              data-testid={`quote-material-${m.id}`}
              onClick={() => set({ materialType: m.id })}
              className={cn(
                "flex flex-col rounded-2xl border p-4 text-left transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--strato-focus-ring)]",
                active
                  ? "border-strato-blue bg-[var(--strato-blue-soft)]"
                  : "border-strato-gray-100 bg-strato-white hover:border-strato-gray-300",
              )}
            >
              <span className="grid h-9 w-9 place-items-center rounded-lg border border-strato-gray-100 bg-strato-gray-50 text-strato-blue">
                <Icon className="h-4 w-4" strokeWidth={1.6} />
              </span>
              <span className="mt-3 text-sm font-medium text-strato-graphite">{m.label}</span>
              <span className="mt-1 text-xs text-muted-foreground">{m.note}</span>
            </button>
          );
        })}
      </div>
      {data.materialType === "custom" ? (
        <input
          data-testid="quote-material-request"
          value={data.materialRequest}
          onChange={(e) => set({ materialRequest: e.target.value })}
          placeholder="Indica il materiale che vorresti valutare"
          className="quote-input mt-4"
        />
      ) : null}

      <p className="mt-8 text-sm font-medium text-strato-graphite">Colore</p>
      <div className="mt-3 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        {QUOTE_MATERIAL.colors.map((c, i) => {
          const active = !data.customColor && data.color === i;
          return (
            <button
              key={c.name}
              type="button"
              data-testid={`quote-color-${i}`}
              onClick={() => set({ color: i, customColor: false })}
              className={cn(
                "rounded-2xl border p-4 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--strato-focus-ring)]",
                active
                  ? "border-strato-blue bg-[var(--strato-blue-soft)]"
                  : "border-strato-gray-100 bg-strato-white hover:border-strato-gray-300",
              )}
            >
              <div className="mx-auto aspect-square w-full overflow-hidden rounded-xl bg-strato-offwhite">
                <img
                  src={MATERIAL_IMAGES[c.name]}
                  alt={`PLA ${c.name}`}
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
              </div>
              <p className="mt-3 text-center text-xs font-medium text-strato-graphite">
                {c.name}
              </p>
            </button>
          );
        })}
        <button
          type="button"
          data-testid="quote-color-custom"
          onClick={() => set({ customColor: true })}
          className={cn(
            "rounded-2xl border p-4 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--strato-focus-ring)]",
            data.customColor
              ? "border-strato-blue bg-[var(--strato-blue-soft)]"
              : "border-strato-gray-100 bg-strato-white hover:border-strato-gray-300",
          )}
        >
          <div className="mx-auto grid aspect-square w-full place-items-center rounded-xl border border-dashed border-strato-gray-300 bg-strato-offwhite text-strato-blue">
            <Palette className="h-6 w-6" strokeWidth={1.5} />
          </div>
          <p className="mt-3 text-center text-xs font-medium text-strato-graphite">
            Altro colore
          </p>
        </button>
      </div>
      {data.customColor ? (
        <input
          data-testid="quote-color-request"
          value={data.colorRequest}
          onChange={(e) => set({ colorRequest: e.target.value })}
          placeholder="Scrivi il colore che hai in mente"
          className="quote-input mt-4"
        />
      ) : null}
      <p className="mt-3 text-xs text-muted-foreground">
        Se il colore non è disponibile subito, verrà confermato nel preventivo tecnico.
      </p>

      <p className="mt-8 text-sm font-medium text-strato-graphite">Finitura</p>
      <div className="mt-3 flex flex-wrap gap-3">
        {FINISH_OPTIONS.map((opt, i) => {
          const active = data.finish === opt;
          return (
            <button
              key={opt}
              type="button"
              data-testid={`quote-finish-${i}`}
              onClick={() => set({ finish: opt })}
              className={cn(
                "rounded-full border px-5 py-2.5 text-sm font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--strato-focus-ring)]",
                active
                  ? "border-strato-blue bg-strato-blue text-white"
                  : "border-strato-gray-100 bg-strato-white text-strato-graphite hover:border-strato-gray-300",
              )}
            >
              {opt}
            </button>
          );
        })}
      </div>
    </StepWrap>
  );

  /* ================= STEP 6 — quantity & timing ================= */
  const StepQuantity = (
    <StepWrap
      micro="STEP 06 · LOTTO"
      title="Quantità e tempi"
      subtitle="Indica un valore di partenza. La quantità finale può essere affinata in fase di preventivo."
    >
      <div className="flex items-center gap-4">
        <button
          type="button"
          data-testid="quote-qty-dec"
          onClick={() => set({ quantity: Math.max(1, data.quantity - 1) })}
          className="grid h-12 w-12 place-items-center rounded-xl border border-strato-gray-100 bg-strato-white text-strato-graphite transition-colors hover:border-strato-gray-300"
        >
          <Minus className="h-4 w-4" />
        </button>
        <input
          data-testid="quote-qty-input"
          type="number"
          min={1}
          value={data.quantity}
          onChange={(e) =>
            set({ quantity: Math.max(1, parseInt(e.target.value || "1", 10)) })
          }
          className="h-12 w-24 rounded-xl border border-strato-gray-100 bg-strato-white text-center font-display text-lg text-strato-graphite outline-none focus:border-strato-blue focus-visible:ring-2 focus-visible:ring-[var(--strato-focus-ring)]"
        />
        <button
          type="button"
          data-testid="quote-qty-inc"
          onClick={() => set({ quantity: data.quantity + 1 })}
          className="grid h-12 w-12 place-items-center rounded-xl border border-strato-gray-100 bg-strato-white text-strato-graphite transition-colors hover:border-strato-gray-300"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>
      <div className="mt-5 flex flex-wrap gap-2">
        {QUANTITY_PRESETS.map((q, i) => (
          <button
            key={q}
            type="button"
            data-testid={`quote-qty-preset-${i}`}
            onClick={() => set({ quantity: q })}
            className={cn(
              "rounded-lg border px-4 py-2 text-sm font-medium transition-colors",
              data.quantity === q
                ? "border-strato-blue text-strato-blue"
                : "border-strato-gray-100 text-muted-foreground hover:border-strato-gray-300",
            )}
          >
            {q} pz
          </button>
        ))}
      </div>

      <p className="mt-8 text-sm font-medium text-strato-graphite">Urgenza</p>
      <div className="mt-3 flex flex-wrap gap-3">
        {URGENCY_OPTIONS.map((opt, i) => {
          const active = data.urgency === opt;
          return (
            <button
              key={opt}
              type="button"
              data-testid={`quote-urgency-${i}`}
              onClick={() => set({ urgency: opt })}
              className={cn(
                "rounded-full border px-5 py-2.5 text-sm font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--strato-focus-ring)]",
                active
                  ? "border-strato-blue bg-strato-blue text-white"
                  : "border-strato-gray-100 bg-strato-white text-strato-graphite hover:border-strato-gray-300",
              )}
            >
              {opt}
            </button>
          );
        })}
      </div>
      {data.urgency === "Ho una data specifica" ? (
        <div className="mt-4">
          <label className="mb-2 block text-sm font-medium text-strato-graphite">
            Data o periodo desiderato
          </label>
          <input
            data-testid="quote-desired-date"
            value={data.desiredDate}
            onChange={(e) => set({ desiredDate: e.target.value })}
            placeholder="Indica la data o il periodo desiderato"
            className="quote-input max-w-sm"
          />
          <p className="mt-2 text-xs text-muted-foreground">
            La data verrà valutata in base al progetto e non è garantita automaticamente.
          </p>
        </div>
      ) : null}
    </StepWrap>
  );

  /* ================= STEP 7 — final notes ================= */
  const StepNotes = (
    <StepWrap
      micro="STEP 07 · DETTAGLI"
      title="Note finali"
      subtitle="Facoltativo. Aggiungi qualsiasi dettaglio utile alla valutazione."
    >
      <label className="mb-2 block text-sm font-medium text-strato-graphite">
        Dettagli aggiuntivi o richieste particolari
      </label>
      <textarea
        data-testid="quote-notes"
        value={data.notes}
        onChange={(e) => set({ notes: e.target.value })}
        rows={5}
        placeholder="Aggiungi vincoli, accoppiamenti, uso finale o richieste particolari."
        className="w-full resize-none rounded-2xl border border-strato-gray-100 bg-strato-white p-5 text-sm leading-[1.6] text-strato-graphite outline-none transition-colors placeholder:text-strato-gray-300 focus:border-strato-blue focus-visible:ring-2 focus-visible:ring-[var(--strato-focus-ring)]"
      />
    </StepWrap>
  );

  /* ================= STEP 8 — contact + summary ================= */
  const isCustomMaterial = data.materialType === "custom";
  const materialLabel =
    data.materialType === "pla"
      ? "PLA"
      : data.materialType === "advice"
        ? "Da consigliare"
        : "Materiale su richiesta";
  const colorLabel = data.customColor ? "Colore su richiesta" : QUOTE_MATERIAL.colors[data.color].name;
  const pieceLabel =
    data.pieceType === "Altro"
      ? "Altro" + (data.pieceTypeOther.trim() ? ` — ${data.pieceTypeOther.trim()}` : "")
      : data.pieceType || "—";
  const useLabel =
    data.use === "Altro"
      ? "Altro" + (data.useOther.trim() ? ` — ${data.useOther.trim()}` : "")
      : data.use || "—";
  const urgencyLabel = data.urgency || "—";
  const showMaterialImage = data.materialType === "pla" && !data.customColor;
  const emailError = data.email.length > 0 && !emailValid(data.email);

  const summaryRows = [
    {
      k: "Punto di partenza",
      v: data.start === "file" ? "Ho un file 3D" : data.start === "nofile" ? "Non ho ancora un file" : "—",
    },
    ...(data.start === "file"
      ? [{ k: "File", v: data.fileStatus === "done" ? data.fileName : "Nessun file caricato" }]
      : [{ k: "Descrizione", v: data.description || "—" }]),
    { k: "Tipo di pezzo", v: pieceLabel },
    { k: "Uso previsto", v: useLabel },
    { k: "Obiettivo", v: data.goal || "—" },
    { k: "Tecnologia", v: "FDM" },
    { k: "Materiale", v: materialLabel },
    ...(isCustomMaterial && data.materialRequest.trim()
      ? [{ k: "Materiale indicato", v: data.materialRequest.trim() }]
      : []),
    { k: "Colore", v: colorLabel },
    ...(data.customColor && data.colorRequest.trim()
      ? [{ k: "Colore indicato", v: data.colorRequest.trim() }]
      : []),
    { k: "Finitura", v: data.finish },
    { k: "Quantità", v: `${data.quantity} pz` },
    { k: "Urgenza", v: urgencyLabel },
    ...(data.urgency === "Ho una data specifica" && data.desiredDate
      ? [{ k: "Data desiderata", v: data.desiredDate }]
      : []),
    ...(data.notes.trim() ? [{ k: "Note finali", v: data.notes.trim() }] : []),
    { k: "Nome", v: data.firstName || "—" },
    { k: "Cognome", v: data.lastName || "—" },
    { k: "Email", v: data.email || "—" },
    { k: "Telefono", v: data.phone || "—" },
    { k: "Azienda", v: data.company || "—" },
  ];

  const StepContact = (
    <StepWrap
      micro="STEP 08 · RICHIESTA"
      title="Contatti e riepilogo"
      subtitle="Lascia un riferimento, controlla i dettagli e invia. Nessun impegno."
    >
      {/* contact fields */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Nome" required>
          <input
            data-testid="quote-first-name"
            value={data.firstName}
            onChange={(e) => set({ firstName: e.target.value })}
            placeholder="Nome"
            className="quote-input"
          />
        </Field>
        <Field label="Cognome">
          <input
            data-testid="quote-last-name"
            value={data.lastName}
            onChange={(e) => set({ lastName: e.target.value })}
            placeholder="Cognome"
            className="quote-input"
          />
        </Field>
        <Field label="Email" required error={emailError ? "Inserisci un indirizzo email valido." : null}>
          <input
            data-testid="quote-email"
            value={data.email}
            onChange={(e) => set({ email: e.target.value })}
            placeholder="nome@dominio.it"
            className={cn("quote-input", emailError && "!border-amber-500")}
          />
        </Field>
        <Field label="Telefono (facoltativo)">
          <input
            data-testid="quote-phone"
            value={data.phone}
            onChange={(e) => set({ phone: e.target.value })}
            placeholder="+39 000 000 0000"
            className="quote-input"
          />
        </Field>
        <Field label="Azienda (facoltativa)">
          <input
            data-testid="quote-company"
            value={data.company}
            onChange={(e) => set({ company: e.target.value })}
            placeholder="Nome azienda"
            className="quote-input"
          />
        </Field>
      </div>

      {/* honeypot — hidden from users */}
      <input
        type="text"
        name="company_website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        value={data.hp}
        onChange={(e) => set({ hp: e.target.value })}
        data-testid="quote-hp"
        className="pointer-events-none absolute left-[-9999px] top-auto h-px w-px overflow-hidden opacity-0"
      />

      {/* summary */}
      <div data-testid="quote-summary" className="mt-8">
        <div className="mb-4 flex items-center gap-4 rounded-2xl border border-strato-gray-100 bg-strato-gray-50 p-4">
          <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-xl bg-strato-offwhite">
            {showMaterialImage ? (
              <img
                src={MATERIAL_IMAGES[QUOTE_MATERIAL.colors[data.color].name]}
                alt={`PLA ${QUOTE_MATERIAL.colors[data.color].name}`}
                className="h-full w-full object-cover"
              />
            ) : (
              <span className="grid h-full w-full place-items-center text-strato-blue">
                {isCustomMaterial ? (
                  <FlaskConical className="h-6 w-6" strokeWidth={1.5} />
                ) : (
                  <Palette className="h-6 w-6" strokeWidth={1.5} />
                )}
              </span>
            )}
          </div>
          <div>
            <MicroLabel className="text-muted-foreground">MATERIALE</MicroLabel>
            <p className="font-display text-lg font-medium text-strato-graphite">
              {materialLabel} · {colorLabel}
            </p>
            <p className="text-xs text-muted-foreground">
              {data.quantity} pz · {pieceLabel === "—" ? "tipo da definire" : pieceLabel}
            </p>
          </div>
        </div>

        <div className="overflow-hidden rounded-2xl border border-strato-gray-100">
          {summaryRows.map((row, i) => (
            <div
              key={row.k}
              className={cn(
                "flex flex-col gap-1 px-5 py-4 sm:flex-row sm:items-start sm:justify-between sm:gap-6",
                i % 2 === 0 ? "bg-strato-white" : "bg-strato-gray-50",
              )}
            >
              <MicroLabel className="text-muted-foreground sm:pt-0.5">{row.k}</MicroLabel>
              <span className="max-w-[52ch] text-sm text-strato-graphite sm:text-right">
                {row.v}
              </span>
            </div>
          ))}
        </div>

        {/* consents */}
        <div data-testid="quote-consents" className="mt-6 space-y-4">
          <label className="flex cursor-pointer items-start gap-3">
            <Checkbox
              data-testid="quote-consent-terms"
              checked={data.acceptTerms}
              onCheckedChange={(v) => set({ acceptTerms: v === true })}
              className="mt-0.5 border-strato-gray-300 data-[state=checked]:border-strato-blue data-[state=checked]:bg-strato-blue"
            />
            <span className="text-sm leading-[1.6] text-strato-graphite">
              Dichiaro di aver letto e accettato i{" "}
              <Link to="/termini-condizioni" target="_blank" rel="noopener noreferrer" data-testid="quote-consent-terms-link" className="font-medium text-strato-blue underline decoration-strato-blue/30 underline-offset-2 hover:decoration-strato-blue">
                Termini e condizioni
              </Link>
              , la{" "}
              <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer" data-testid="quote-consent-privacy-link" className="font-medium text-strato-blue underline decoration-strato-blue/30 underline-offset-2 hover:decoration-strato-blue">
                Privacy Policy
              </Link>{" "}
              e la{" "}
              <Link to="/cookie-policy" target="_blank" rel="noopener noreferrer" data-testid="quote-consent-cookie-link" className="font-medium text-strato-blue underline decoration-strato-blue/30 underline-offset-2 hover:decoration-strato-blue">
                Cookie Policy
              </Link>{" "}
              di Strato Lab 3D.
            </span>
          </label>

          <label className="flex cursor-pointer items-start gap-3">
            <Checkbox
              data-testid="quote-consent-lawful"
              checked={data.acceptLawful}
              onCheckedChange={(v) => set({ acceptLawful: v === true })}
              className="mt-0.5 border-strato-gray-300 data-[state=checked]:border-strato-blue data-[state=checked]:bg-strato-blue"
            />
            <span className="text-sm leading-[1.6] text-strato-graphite">
              Dichiaro che il file, il modello o la descrizione inviati riguardano un
              utilizzo lecito, non pericoloso e compatibile con un servizio di stampa
              3D su richiesta. Dichiaro inoltre di essere responsabile dell'uso finale
              del componente richiesto.
            </span>
          </label>
        </div>

        {submitMsg ? (
          <p data-testid="quote-submit-error" className="mt-4 text-sm text-amber-600">
            {submitMsg}
          </p>
        ) : null}
      </div>
    </StepWrap>
  );

  const steps = [
    StepStart,
    StepInput,
    StepType,
    StepUsage,
    StepMaterial,
    StepQuantity,
    StepNotes,
    StepContact,
  ];

  return (
    <div data-testid="quote-configurator" className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
      {/* Left rail — stepper */}
      <aside className="lg:col-span-4">
        <div className="lg:sticky lg:top-24">
          <MicroLabel className="text-strato-blue">CONFIGURATORE</MicroLabel>
          <h1 className="mt-3 font-display text-3xl font-medium tracking-[-0.02em] text-strato-graphite">
            Avvia il preventivo
          </h1>
          <p className="mt-3 max-w-[38ch] text-sm leading-[1.6] text-muted-foreground">
            Otto passaggi guidati. Ti rispondiamo con una valutazione tecnica entro 12
            ore lavorative.
          </p>

          {/* mobile compact indicator */}
          <div data-testid="quote-step-indicator" className="mt-6 flex items-center gap-2 lg:hidden">
            <span className="font-display text-sm font-medium text-strato-graphite">
              Passaggio {step + 1} di {QUOTE_STEPS.length}
            </span>
            <span className="strato-micro text-muted-foreground">· {QUOTE_STEPS[step].label}</span>
          </div>
          <div className="mt-3 h-1 w-full overflow-hidden rounded-full bg-strato-gray-100 lg:hidden">
            <div
              className="h-full rounded-full bg-strato-blue transition-all duration-300"
              style={{ width: `${((step + 1) / QUOTE_STEPS.length) * 100}%` }}
            />
          </div>

          {/* desktop vertical stepper */}
          <ol className="mt-8 hidden lg:block">
            {QUOTE_STEPS.map((s, i) => {
              const done = i < step;
              const current = i === step;
              return (
                <li key={s.id} className="flex items-start gap-3 pb-5 last:pb-0">
                  <span
                    className={cn(
                      "grid h-7 w-7 flex-shrink-0 place-items-center rounded-full border text-xs font-semibold transition-colors",
                      done
                        ? "border-strato-blue bg-strato-blue text-white"
                        : current
                          ? "border-strato-blue bg-[var(--strato-blue-soft)] text-strato-blue"
                          : "border-strato-gray-200 text-strato-gray-300",
                    )}
                  >
                    {done ? <Check className="h-3.5 w-3.5" strokeWidth={2.5} /> : i + 1}
                  </span>
                  <div className="pt-0.5">
                    <p
                      className={cn(
                        "text-sm font-medium",
                        current ? "text-strato-graphite" : done ? "text-strato-graphite/70" : "text-strato-gray-300",
                      )}
                    >
                      {s.label}
                    </p>
                    <MicroLabel className="text-strato-gray-300">{s.micro}</MicroLabel>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:col-span-8">
        <div className="rounded-3xl border border-strato-gray-100 bg-strato-white p-6 shadow-soft sm:p-9">
          {steps[step]}

          {stepError ? (
            <p data-testid="quote-step-error" className="mt-6 text-sm text-amber-600">
              {stepError}
            </p>
          ) : null}

          {/* nav */}
          <div className="mt-8 flex items-center justify-between border-t border-strato-gray-100 pt-6">
            {step > 0 ? (
              <StratoButton variant="secondary" data-testid="quote-back-button" onClick={back}>
                <ArrowLeft className="h-4 w-4" />
                Indietro
              </StratoButton>
            ) : (
              <StratoButton as={Link} to="/" variant="ghost" data-testid="quote-exit">
                <ArrowLeft className="h-4 w-4" />
                Esci
              </StratoButton>
            )}

            {step < last ? (
              <StratoButton data-testid="quote-next-button" onClick={next}>
                Continua
                <ArrowRight className="h-4 w-4" />
              </StratoButton>
            ) : (
              <StratoButton
                data-testid="quote-submit-button"
                onClick={submit}
                disabled={!data.acceptTerms || !data.acceptLawful}
              >
                Invia richiesta
                <ArrowRight className="h-4 w-4" />
              </StratoButton>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ---------- small helpers ---------- */
const StepWrap = ({ micro, title, subtitle, children }) => (
  <div>
    <MicroLabel className="text-strato-blue">{micro}</MicroLabel>
    <h2 className="mt-3 font-display text-2xl font-medium tracking-[-0.01em] text-strato-graphite sm:text-3xl">
      {title}
    </h2>
    {subtitle ? (
      <p className="mt-3 max-w-[54ch] text-sm leading-[1.6] text-muted-foreground">{subtitle}</p>
    ) : null}
    <div className="mt-7">{children}</div>
  </div>
);

const Field = ({ label, required, error, children }) => (
  <label className="block">
    <span className="mb-2 flex items-center gap-2 text-sm font-medium text-strato-graphite">
      {label}
      {required ? <span className="text-strato-blue">*</span> : null}
    </span>
    {children}
    {error ? <span className="mt-1.5 block text-xs text-amber-600">{error}</span> : null}
  </label>
);

const StateShell = ({ testId, children }) => (
  <div
    data-testid={testId}
    className="mx-auto flex max-w-xl flex-col items-center rounded-3xl border border-strato-gray-100 bg-strato-white p-10 text-center shadow-soft sm:p-14"
  >
    {children}
  </div>
);
